import { useTranslation } from 'react-i18next';

import type { User } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useActionBar, useFlags, useUser } from '@feathr/extender/state';
import type { Languages } from '@feathr/locales';
import { DefaultLanguage, i18n } from '@feathr/locales';
import type { TValidateGrouped } from '@feathr/rachis';

/** Return type for the useProfilePage hook. */
interface IUseProfilePageReturn {
  /** The current user model */
  user: User;
  /** Translation function */
  t: ReturnType<typeof useTranslation>['t'];
  /** Feature flags */
  flags: ReturnType<typeof useFlags>;
  /** Validation errors for profile fields */
  errors: TValidateGrouped;
  /** Handler for language select changes */
  handleLanguageChange: (lng?: Languages) => void;
  /** Handler to save all profile settings */
  handleSave: () => Promise<void>;
  /** Action bar setter for left actions */
  setLeftActions: ReturnType<typeof useActionBar>['setLeftActions'];
  /** Action bar setter for right actions */
  setRightActions: ReturnType<typeof useActionBar>['setRightActions'];
  /** Whether the user model has unsaved changes */
  isDirty: boolean;
}

/**
 * Custom hook that encapsulates all state management and business logic for the User ProfilePage component.
 *
 * Handles user profile editing including name, avatar, language, booking link, and timezone.
 *
 * @returns Object containing state values, handlers, and validation errors
 *
 * @example
 * ```tsx
 * const { user, t, flags, errors, handleLanguageChange, handleSave } = useProfilePage();
 * ```
 */
export function useProfilePage(): IUseProfilePageReturn {
  const flags = useFlags();
  const user = useUser();
  const { t } = useTranslation();
  const { setLeftActions, setRightActions } = useActionBar();

  function handleLanguageChange(lng: Languages = 'en-US'): void {
    user?.setSetting('lng', lng);
  }

  async function handleSave(): Promise<void> {
    try {
      const savedUser = (await user.patchDirty()) as User;
      const lng = savedUser.getSetting('lng', DefaultLanguage);
      if (i18n.language !== lng) {
        i18n.changeLanguage(lng);
        document.documentElement.lang = lng;
      }
      toast(t('Your settings were saved.'), { type: 'success' });
    } catch {
      toast(t('There was an error saving your settings.'), { type: 'error' });
    }
  }

  const errors = user.validate(
    ['picture', 'calendly_link', 'first_name', 'last_name'],
    false,
    'grouped',
  ).errors;

  return {
    user,
    t,
    flags,
    errors,
    handleLanguageChange,
    handleSave,
    setLeftActions,
    setRightActions,
    isDirty: user.isDirty,
  };
}
